import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Vendor compliance statuses (Module 3)
const vendorStatusConfig = {
  compliant: {
    className: 'bg-[hsl(142_56%_92%)] text-[hsl(142_56%_22%)] border-[hsl(142_40%_78%)] hover:bg-[hsl(142_56%_88%)]',
    label: 'Compliant'
  },
  non_compliant: {
    className: 'bg-[hsl(0_85%_94%)] text-[hsl(0_72%_34%)] border-[hsl(0_70%_82%)] hover:bg-[hsl(0_85%_90%)]',
    label: 'Non-Compliant'
  },
  missing_documents: {
    className: 'bg-[hsl(36_100%_92%)] text-[hsl(28_95%_26%)] border-[hsl(36_80%_76%)] hover:bg-[hsl(36_100%_88%)]',
    label: 'Missing Docs'
  },
  expired_documents: {
    className: 'bg-[hsl(0_85%_94%)] text-[hsl(0_72%_34%)] border-[hsl(0_70%_82%)] hover:bg-[hsl(0_85%_90%)]',
    label: 'Expired Docs'
  },
  pending_review: {
    className: 'bg-[hsl(197_80%_92%)] text-[hsl(197_78%_22%)] border-[hsl(197_55%_78%)] hover:bg-[hsl(197_80%_88%)]',
    label: 'Pending Review'
  },
  needs_attention: {
    className: 'bg-[hsl(36_100%_92%)] text-[hsl(28_95%_26%)] border-[hsl(36_80%_76%)] hover:bg-[hsl(36_100%_88%)]',
    label: 'Needs Attention'
  },
  // Backward-compatible statuses
  warning: {
    className: 'bg-[hsl(36_100%_92%)] text-[hsl(28_95%_26%)] border-[hsl(36_80%_76%)] hover:bg-[hsl(36_100%_88%)]',
    label: 'Warning'
  },
  expired: {
    className: 'bg-[hsl(0_85%_94%)] text-[hsl(0_72%_34%)] border-[hsl(0_70%_82%)] hover:bg-[hsl(0_85%_90%)]',
    label: 'Expired'
  },
  pending: {
    className: 'bg-[hsl(197_80%_92%)] text-[hsl(197_78%_22%)] border-[hsl(197_55%_78%)] hover:bg-[hsl(197_80%_88%)]',
    label: 'Pending'
  },
  info: {
    className: 'bg-[hsl(197_80%_92%)] text-[hsl(197_78%_22%)] border-[hsl(197_55%_78%)] hover:bg-[hsl(197_80%_88%)]',
    label: 'Info'
  }
};

export const VendorStatusPill = ({ status, className }) => {
  // Try compliance_status first, then legacy status
  const config = vendorStatusConfig[status] || vendorStatusConfig.pending;
  return (
    <Badge
      data-testid="vendor-status-pill"
      variant="outline"
      className={cn('rounded-full px-2.5 py-1 text-xs font-medium border transition-colors duration-200', config.className, className)}
    >
      {config.label}
    </Badge>
  );
};

// Document state machine statuses (Module 3)
const docStateConfig = {
  uploaded: {
    className: 'bg-[hsl(197_80%_92%)] text-[hsl(197_78%_22%)] border-[hsl(197_55%_78%)]',
    label: 'Uploaded'
  },
  pending_review: {
    className: 'bg-[hsl(36_100%_92%)] text-[hsl(28_95%_26%)] border-[hsl(36_80%_76%)]',
    label: 'Pending Review'
  },
  valid: {
    className: 'bg-[hsl(142_56%_92%)] text-[hsl(142_56%_22%)] border-[hsl(142_40%_78%)]',
    label: 'Valid'
  },
  expired: {
    className: 'bg-[hsl(0_85%_94%)] text-[hsl(0_72%_34%)] border-[hsl(0_70%_82%)]',
    label: 'Expired'
  },
  invalid: {
    className: 'bg-[hsl(0_85%_94%)] text-[hsl(0_72%_34%)] border-[hsl(0_70%_82%)]',
    label: 'Invalid'
  },
  missing: {
    className: 'bg-[hsl(36_100%_92%)] text-[hsl(28_95%_26%)] border-[hsl(36_80%_76%)]',
    label: 'Missing'
  },
  // Backward compat from extraction_status
  queued: {
    className: 'bg-[hsl(197_80%_92%)] text-[hsl(197_78%_22%)] border-[hsl(197_55%_78%)]',
    label: 'Queued'
  },
  processing: {
    className: 'bg-[hsl(197_80%_92%)] text-[hsl(197_78%_22%)] border-[hsl(197_55%_78%)]',
    label: 'Processing'
  },
  completed: {
    className: 'bg-[hsl(142_56%_92%)] text-[hsl(142_56%_22%)] border-[hsl(142_40%_78%)]',
    label: 'Completed'
  },
  approved: {
    className: 'bg-[hsl(142_56%_92%)] text-[hsl(142_56%_22%)] border-[hsl(142_40%_78%)]',
    label: 'Approved'
  },
  failed: {
    className: 'bg-[hsl(0_85%_94%)] text-[hsl(0_72%_34%)] border-[hsl(0_70%_82%)]',
    label: 'Failed'
  },
};

export const DocStatusBadge = ({ status, className }) => {
  const config = docStateConfig[status] || docStateConfig.uploaded;
  return (
    <Badge
      data-testid="doc-status-badge"
      variant="outline"
      className={cn('rounded-full px-2.5 py-1 text-xs font-medium border transition-colors duration-200', config.className, className)}
    >
      {config.label}
    </Badge>
  );
};

// Helper to get best vendor status from either compliance_status or legacy status
export const getDisplayStatus = (vendor) => {
  return vendor?.compliance_status || vendor?.status || 'pending';
};
